import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mic, Target, Heart, BookOpen, Building2, GraduationCap, Church } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { PILLARS } from "../lib/constants";

const HOST_IMG = "/dennis.jpg";

export default function About() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "citizen_voices", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a short message");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/guest-requests", form);
      toast.success(res.data.message);
      setDone(true);
      setForm({ name: "", email: "", phone: "", topic: "citizen_voices", message: "" });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Please check your details and try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#050B14] min-h-screen" data-testid="about-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-5 sm:px-8 grain">
        <div className="max-w-5xl mx-auto">
          <div className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#0066FF] mb-3">About The Show</div>
          <h1 className="font-display text-5xl sm:text-6xl uppercase leading-[0.9]">
            The Story Behind <br /><span className="text-[#0066FF] blue-glow">The Other Side of Blue</span>
          </h1>
        </div>
      </section>

      {/* Host bio */}
      <section className="py-14 px-5 sm:px-8 bg-[#0C1424]">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[300px_1fr] gap-10 items-start">
          <div>
            <div className="relative rounded-2xl overflow-hidden border border-[#0066FF]/25">
              <img src={HOST_IMG} alt="Dennis Haymon" className="w-full aspect-[3/4] object-cover" data-testid="host-photo" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#050B14] to-transparent p-4">
                <div className="font-cond font-bold uppercase text-xl">Dennis Haymon</div>
                <div className="font-mono2 text-[10px] text-[#FF6B00] uppercase tracking-wide">Host &amp; Producer</div>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Mic className="h-5 w-5 text-[#0066FF]" />
              <h2 className="font-cond font-bold uppercase text-2xl">Meet Your Host</h2>
            </div>
            <div className="space-y-4 text-[#CBD5E1] leading-relaxed" data-testid="host-bio">
              <p>
                Dennis Haymon was born and raised in downtown urban <span className="text-white">St. Louis, Missouri</span> —
                the city whose streets, struggles, and spirit shaped his voice. He is the host and producer of
                <span className="text-white"> The Other Side of Blue — St. Louis</span>, a podcast built to bridge the gap
                between law enforcement and the community through truth, respect, and solutions.
              </p>
              <p>
                A devoted father of five and grandfather of thirteen — with five great-grandchildren — Dennis knows both
                the weight of loss and the power of hope. He has laid to rest his youngest daughter, his brother
                (who passed on her birthday), and both of his parents, who were blind and led with unshakable faith.
                Through it all, <span className="text-[#FF6B00]">he remains hopeful.</span>
              </p>
              <p>
                Ordained a <span className="text-white">Senior Elder in 2009 by Bishop Scott at Believers Temple World
                Fellowship</span>, Dennis is a community activist, published author, entrepreneur, and mentor. As a
                Certified Youth Gang Specialist and founder of "A Few Good Men Plus I," he has devoted his life to
                reaching those the system too often overlooks — turning pain into purpose and stories into change.
              </p>
            </div>

            {/* Credentials */}
            <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="host-credentials">
              <CredCard icon={Church} title="Faith & Community" items={[
                "Ordained Senior Elder — Believers Temple World Fellowship (2009)",
                "Community Activist",
                "Founder & CEO — A Few Good Men Plus I",
              ]} />
              <CredCard icon={BookOpen} title="Published Author" items={[
                "C.R.E.A.M. (Rises To The Top)",
                "From Guns To Redemption",
              ]} />
              <CredCard icon={GraduationCap} title="Education & Certification" items={[
                "Attended University of Missouri–St. Louis (UMSL)",
                "Certified Youth Gang Specialist",
              ]} />
              <CredCard icon={Building2} title="Businesses" items={[
                "HOPE CDS (Construction Development Service)",
                "Haymon Travels LLC",
                "Haymon Productions LLC",
                "Safe & Sound Security LLC",
                "The Haymon Collection LLC",
              ]} />
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 px-5 sm:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-8">
            <Target className="h-5 w-5 text-[#FF6B00]" />
            <h2 className="font-display text-3xl sm:text-4xl uppercase">Our Mission</h2>
          </div>
          <p className="text-lg text-[#CBD5E1] leading-relaxed max-w-3xl mb-10">
            To bridge the gap between law enforcement and the community through truth, respect, and solutions —
            giving every voice a platform and turning hard conversations into real, lasting change in St. Louis.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.id} className="rounded-xl border border-[#0066FF]/15 bg-[#121D33] p-5">
                  <Icon className="h-6 w-6 mb-3" style={{ color: p.color }} />
                  <div className="font-cond font-bold uppercase">{p.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Be a Guest */}
      <section id="be-a-guest" className="py-16 px-5 sm:px-8 bg-[#0C1424] grain">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex justify-center mb-3">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FF6B00]/15 border border-[#FF6B00]/40">
                <Heart className="h-7 w-7 text-[#FF6B00]" />
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none">Be a Guest</h2>
            <p className="mt-4 text-[#94A3B8] max-w-xl mx-auto">
              Have a story the city needs to hear? Whether you're a citizen, an officer, an advocate, or someone
              who's lived it — request a seat at the mic.
            </p>
          </div>

          <form onSubmit={submit} className="rounded-2xl border border-[#0066FF]/25 bg-[#050B14] p-6 sm:p-8 space-y-4" data-testid="guest-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-widest text-[#94A3B8] block mb-1.5">Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="guest-name-input" className="w-full rounded-lg bg-[#0C1424] border border-[#0066FF]/25 px-3.5 py-3 text-sm outline-none focus:border-[#0066FF]" />
              </div>
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-widest text-[#94A3B8] block mb-1.5">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="guest-email-input" className="w-full rounded-lg bg-[#0C1424] border border-[#0066FF]/25 px-3.5 py-3 text-sm outline-none focus:border-[#0066FF]" />
              </div>
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-widest text-[#94A3B8] block mb-1.5">Phone (optional)</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="guest-phone-input" className="w-full rounded-lg bg-[#0C1424] border border-[#0066FF]/25 px-3.5 py-3 text-sm outline-none focus:border-[#0066FF]" />
              </div>
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-widest text-[#94A3B8] block mb-1.5">Topic</label>
                <select value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="guest-topic-select" className="w-full rounded-lg bg-[#0C1424] border border-[#0066FF]/25 px-3.5 py-3 text-sm outline-none focus:border-[#0066FF]">
                  {PILLARS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="font-mono2 text-[10px] uppercase tracking-widest text-[#94A3B8] block mb-1.5">Tell us your story</label>
              <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="guest-message-input" placeholder="What would you like to talk about on the show?" className="w-full rounded-lg bg-[#0C1424] border border-[#0066FF]/25 px-3.5 py-3 text-sm outline-none focus:border-[#0066FF] resize-none" />
            </div>
            <button type="submit" disabled={loading} data-testid="guest-submit-button" className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#0066FF] hover:bg-[#2B82FF] disabled:opacity-60 transition-colors px-5 py-3.5 font-cond font-bold uppercase tracking-wide text-white blue-box-glow">
              <Send className="h-4 w-4" /> {done ? "Request Sent ✓" : loading ? "Sending..." : "Request to Be a Guest"}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const CredCard = ({ icon: Icon, title, items }) => (
  <div className="rounded-xl border border-[#0066FF]/15 bg-[#121D33] p-4">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 text-[#0066FF]" />
      <span className="font-cond font-bold uppercase text-sm tracking-wide">{title}</span>
    </div>
    <ul className="space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2 text-sm text-[#CBD5E1]">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#FF6B00]" />
          {it}
        </li>
      ))}
    </ul>
  </div>
);
